import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Employee } from "@shared/schema";
import { formatDateSafe } from "@/lib/utils";
import { formatStatus } from "@/lib/employeeStatus";
import { Building2, Calendar, DollarSign, Clock, Briefcase } from "lucide-react";

interface EmploymentDetailsScreenProps {
  employee: Employee;
  onComplete: () => void;
  onSkip: () => void;
}

export function EmploymentDetailsScreen({
  employee,
  onComplete,
  onSkip,
}: EmploymentDetailsScreenProps) {
  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatEmploymentType = (type: string | null | undefined) => {
    if (!type) return "N/A";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-muted-foreground">
          Please review your employment information. Contact your HR
          administrator if you notice any errors.
        </p>
      </div>

      <div className="grid gap-4">
        {/* Employment Status */}
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Briefcase className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium mb-1">Employment Status</h4>
              <div className="flex items-center gap-2">
                <Badge variant={employee.status === "active" ? "default" : "secondary"}>
                  {formatStatus(employee.status)}
                </Badge>
                {employee.employmentType && (
                  <span className="text-sm text-muted-foreground">
                    • {formatEmploymentType(employee.employmentType)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Position & Department */}
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium mb-1">Position & Department</h4>
              <div className="space-y-1">
                {employee.position && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Title:</span>{" "}
                    {employee.position}
                  </p>
                )}
                {employee.department && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Department:</span>{" "}
                    {employee.department}
                  </p>
                )}
                {employee.employeeId && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Employee ID:</span>{" "}
                    {employee.employeeId}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Hire Date & Hours */}
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium mb-1">Work Schedule</h4>
              <div className="space-y-1">
                {employee.hireDate && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Hire Date:</span>{" "}
                    {formatDateSafe(employee.hireDate)}
                  </p>
                )}
                {employee.scheduledHours !== null &&
                  employee.scheduledHours !== undefined && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">
                        Scheduled Hours:
                      </span>{" "}
                      {employee.scheduledHours} hours/week
                    </p>
                  )}
              </div>
            </div>
          </div>
        </Card>

        {/* Compensation */}
        {(employee.salary || employee.compensationAmount) && (
          <Card className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">Compensation</h4>
                <div className="space-y-1">
                  {employee.salary && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Salary:</span>{" "}
                      {formatCurrency(employee.salary)}
                    </p>
                  )}
                  {employee.compensationAmount && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Amount:</span>{" "}
                      {formatCurrency(employee.compensationAmount)}
                    </p>
                  )}
                  {employee.compensationType && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Type:</span>{" "}
                      {formatEmploymentType(employee.compensationType)}
                    </p>
                  )}
                  {employee.payCycle && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">Pay Cycle:</span>{" "}
                      {formatEmploymentType(employee.payCycle)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Additional Info */}
        {employee.address && (
          <Card className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">Work Location</h4>
                <p className="text-sm text-muted-foreground">
                  {employee.address}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Information Banner */}
      <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          If you notice any discrepancies in your employment information, please
          contact your HR department or supervisor to request corrections.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onSkip}>
          Skip
        </Button>
        <Button onClick={onComplete}>Continue</Button>
      </div>
    </div>
  );
}
